import { useRef } from 'react'
import AnimalImage from '../components/AnimalImage.jsx'
import ReadAloud from '../components/ReadAloud.jsx'

/**
 * SPEC screen 4: swipe or tap through fact pages. Illustration left, photo
 * right (dinosaurs, and a storybook animal, get a "no photos exist" placeholder),
 * text below, read-aloud.
 * Age 6 pages surface the story's vocabulary word.
 *
 * Every page comes from roster.json and is on screen the moment the kid taps
 * Fight (D-071, D-099), so nothing here ever waits on the model. The battle
 * door (`onDone`) is where the story is needed, and App.jsx covers any wait
 * there with the interstitial rather than a disabled button.
 *
 * The page index is App's, not this screen's (D-127): Back out of the battle's
 * first beat lands on the page the kid left, and Back on the first page goes on
 * out to the matchup instead of being a greyed-out dead end.
 */
/**
 * What the photo frame is showing. A credit means a real photograph is there —
 * of an animal, or of a person on the roster (D-124, D-150). Without one the
 * frame is the placeholder, and there are three reasons it can be there: no
 * photograph of this animal exists, one exists and has not been fetched yet
 * (D-164 — a newly added living animal, until `npm run images:photos` runs), or
 * the record was never photographable in the first place — a storybook animal
 * lives in stories, a hero was drawn rather than photographed (D-168), and a
 * screen character is a character (D-181). The
 * placeholder's own words are "Photo coming", so a living animal's alt text says
 * the same thing rather than telling a screen reader that deer cannot be
 * photographed; the three made-up kinds are checked first, because they are not
 * extinct either and would otherwise be told a photo is on its way.
 * The credit itself is on the credits screen, not over the picture (D-143).
 */
function photoAlt(animal) {
  if (animal.images.photo_attribution) return animal.person ? `Photo of ${animal.name}` : `Photo of a ${animal.name}`
  if (animal.hero) return 'No photos exist: this one was drawn, not photographed'
  if (animal.character) return 'No photos exist: this one is a character in a television show'
  if (animal.era === 'legend') return 'No photos exist: this animal lives in stories'
  if (!animal.extinct) return `Photo coming: a real photo of a ${animal.name} goes here`
  return 'No photos exist of this animal'
}

export default function Facts({ pages, vocab, A, B, age, page: i, onPage, onBack, onDone }) {
  const setI = onPage
  const page = pages[i]
  const animal = page.animal === 'a' ? A : B
  const last = i === pages.length - 1
  const showsVocab = vocab && page.text.toLowerCase().includes(vocab.word.toLowerCase())

  const next = () => (last ? onDone() : setI(i + 1))
  const prev = () => (i === 0 ? onBack() : setI(i - 1))

  // Swipe: a horizontal drag of 60px+ turns the page.
  const startX = useRef(null)
  const onDown = (e) => (startX.current = e.clientX)
  const onUp = (e) => {
    if (startX.current === null) return
    const dx = e.clientX - startX.current
    startX.current = null
    if (dx < -60) next()
    else if (dx > 60) prev()
  }

  return (
    <section className="screen facts" onPointerDown={onDown} onPointerUp={onUp}>
      <div className="facts__head">
        <h1 className={`facts__name facts__name--${page.animal}`}>{animal.name}</h1>
        <span className="facts__nick">{animal.nickname}</span>
        <div className="facts__dots" aria-label={`Page ${i + 1} of ${pages.length}`}>
          {pages.map((_, n) => <span key={n} className={`facts__dot${n === i ? ' is-on' : ''}`} />)}
        </div>
      </div>

      <div className="facts__body">
        <div className="facts__art" key={`art-${i}`}>
          <AnimalImage slug={animal.slug} pose={page.pose} alt={animal.name} />
        </div>
        <div className="facts__art facts__art--photo" key={`photo-${i}`}>
          <AnimalImage slug={animal.slug} kind="photo" alt={photoAlt(animal)} />
          {/* No credit line over the photo any more: the licences are satisfied
              by the credits page, and a caption cost the picture its bottom
              third — on the portrait photos it covered half the animal (D-143). */}
        </div>
        <ReadAloud key={i} text={page.text} auto={age !== '6'} className="facts__text" size="lg" />
        {showsVocab && (
          <div className="vocab-card">
            <span className="vocab-card__tag">New word</span>
            <strong>{vocab.word}</strong>
            <span>{vocab.definition}</span>
          </div>
        )}
      </div>

      <div className="facts__nav">
        <button className="btn" onClick={prev}>◀ Back</button>
        <button className={`btn ${last ? 'btn--fight' : 'btn--primary'}`} onClick={next}>
          {last ? 'To the battle!' : 'Next ▶'}
        </button>
      </div>
    </section>
  )
}
